// src/components/aols/aeo/OLSupdates/OLSupdatesUp.tsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Layout, Breadcrumb } from "antd";
import { Helmet } from "react-helmet-async";
import { HomeOutlined } from "@ant-design/icons";
import logo from "/logo.jpg";
import { Footer } from "../../../dev/utils/Footer";

const FullHeightLayout = styled(Layout)`
  min-height: 100vh;
  background: #000000;
`;

const ContentSection = styled.section`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  background: #000000;
  color: #fff;
  position: relative;
`;

const Logo = styled.img`
  max-width: 200px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
`;

const UpdateList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const UpdateItem = styled.li`
  margin-bottom: 10px;
  color: #fff;
  line-height: 1.6;

  a {
    color: #ffaa00;
    text-decoration: none;
    padding: 5px 10px;
    display: block;
    border: 1px solid #ffaa00;
    border-radius: 4px;
    transition: background 0.3s, color 0.3s;

    &:hover {
      background: #ffaa00;
      color: #000;
    }

    &:focus {
      outline: 2px solid #40c4ff;
      outline-offset: 2px;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

// Define the structure for upcoming updates
export interface UpcomingUpdate {
  id: number;
  text: string;
  date: string; // ISO date string (e.g., "2025-04-17")
  thumbnail?: string;
  url?: string;
}

// Sample upcoming updates data (replace with actual data source as needed)
export const upcomingUpdates: UpcomingUpdate[] = [
  
  {
    id: 2923,
    text: "OLSLM: LIVE active Meditation",
    date: "2025-04-25",
    thumbnail: "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/img-public%2F2920.OLSLM%20logo.png?alt=media&token=34b012f0-92fa-4c1b-beee-bdaacfc7eddd",
    url: "https://youtube.com/live/KI3UOWyhn8Q",
  },
   {
    id: 2924,
    text: "OLSLM: LIVE active Meditation",
    date: "2025-04-26",
    thumbnail: "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2FOLSLM%20asset%20logo.png?alt=media&token=3d9004c4-b830-4530-9ee8-2d3c1878cfbb",
    url: "https://youtube.com/live/Dxm8VFXeGOc",
  },
  {
    id: 2925,
    text: "OLSLM: LIVE active Meditation",
    date: "2025-04-27",
    thumbnail: "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2FOLSLM%20asset%20logo.png?alt=media&token=3d9004c4-b830-4530-9ee8-2d3c1878cfbb",
    url: "https://youtube.com/live/JsXddbbgR-E",
  },
  
  
];

const OLSupdatesUp: React.FC = () => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OneLightSystem OLS",
      alternateName: "OLS Aunselo",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
      description: "OneLightSystem OLS provides upcoming updates on meditation education, spiritual growth, and asset announcements through Sun Light Meditation programs.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Belmont",
        addresseertRegion: "CA",
        addressCountry: "US",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "contact@olsme.com",
      },
      sameAs: [
        "https://www.linkedin.com/company/onelightsystem",
        "https://twitter.com/onelightsystem",
        "https://www.facebook.com/onelightsystem",
        "https://www.instagram.com/onelightsystem",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.olsme.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "OLS Updates",
          item: "https://www.olsme.com/olsupdates",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Upcoming Updates",
          item: "https://www.olsme.com/olsupdates/upcoming",
        },
      ],
    },
  ];

  return (
    <FullHeightLayout className="full-height-layout">
      <Helmet>
        <title>Upcoming OLS Updates | OneLightSystem - Meditation & Asset Announcements</title>
        <meta
          name="description"
          content="Discover upcoming updates from OneLightSystem (OLS) on olsme.com. Stay informed about future events, meditation education, and asset announcements."
        />
        <meta
          name="keywords"
          content="Upcoming OLS Updates, OneLightSystem, OLS iee.aeo, Sun Light Meditation, Light Science, OLSLM assets, meditation education, spiritual growth, meditation events, asset announcements"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="Upcoming OLS Updates | OneLightSystem - Meditation & Asset Announcements" />
        <meta
          property="og:description"
          content="Discover upcoming updates from OneLightSystem (OLS) on olsme.com. Stay informed about future events, meditation education, and asset announcements."
        />
        <meta property="og:url" content="https://www.olsme.com/olsupdates/upcoming" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="Upcoming OLS Updates | OneLightSystem - Meditation & Asset Announcements" />
        <meta
          name="twitter:description"
          content="Discover upcoming updates from OneLightSystem (OLS) on olsme.com. Stay informed about future events, meditation education, and asset announcements."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <link rel="canonical" href="https://www.olsme.com/olsupdates/upcoming" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined /> Home
                </Link>
              ),
            },
            {
              title: <Link to="/olsupdates">OLS Updates</Link>,
            },
            {
              title: "Upcoming Updates",
            },
          ]}
        />
      </BreadcrumbWrapper>
      <ContentSection className="content-section">
        <div className="content">
          <a href="https://www.olsme.com" aria-label="OneLightSystem Home">
            <Logo src={logo} alt="OneLightSystem OLS Logo" className="logo" />
          </a>

          <Title level={1} className="section-title">
            Upcoming OLS Updates
          </Title>
          <Paragraph className="content-text">
            Get a sneak peek at what’s coming next for OneLightSystem (OLS).
             Stay tuned for future events, meditation education programs, and asset announcements.
          </Paragraph>

          <div className="feature-card">
            <UpdateList className="update-list">
              {upcomingUpdates.map((update) => (
                <UpdateItem className="update-item" key={update.id}>
                  <a href={update.url} target="_blank" rel="noopener noreferrer">
                    {update.id}. {update.text} - {new Date(update.date).toLocaleDateString()}
                  </a>
                </UpdateItem>
              ))}
            </UpdateList>
          </div>

          <div className="social-follow-wrapper">
            <a href="https://github.com/sponsors/onelightsystem" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/github.svg" alt="GitHub" />
            </a>
            <a href="https://www.blogger.com/profile/09343545978643567973" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/blogger.svg" alt="Blogger" />
            </a>
            <a href="https://www.facebook.com/onelightsystemols" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/facebook.svg" alt="Facebook" />
            </a>
            <a href="https://www.linkedin.com/company/one-light-system" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/linkedin.svg" alt="LinkedIn" />
            </a>
            <a href="https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/youtube.svg" alt="YouTube" />
            </a>
            <a href="https://x.com/onelightsystem" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/x.svg" alt="X" />
            </a>
          </div>
        </div>
      </ContentSection>
      <Footer />
    </FullHeightLayout>
  );
};

export default OLSupdatesUp;